'use client';

import { useContext, useEffect, useState } from 'react';

import { useAuth } from '@r123/auth';
import { useTracker } from '@r123/segment-tracker';
import { getCookie } from 'cookies-next';
import { v4 } from 'uuid';

import R123EnquiryContext from '@/modules/enquiry/r123-enquiry.context';
import gtag from '@/modules/gtag/gtag';
import { GA4EventActionEnum } from '@/modules/tracker/tracker.type';

import { useListingTracker } from '../[lang]/(listing)/properti/_components/ListingTrackerProvider';
import { useListingTrackerGA } from '../[lang]/(listing)/properti/_tracker_ga/ListingTrackerGAProvider';
import { AuthUserRoleEnum } from '../_constant/authType';
import { getDeviceType } from '../_helper/deviceType';
import { useMediaQuery } from '../_hooks/useMediaQuery';
import { GA4FeatureType, GA4PageType } from './GA4TrackerType';
import { TrackerPlacementName } from './TrackerPlacementName';
import { ListingEvents, Trackers, eventMappings } from './eventMapping';
import { sendGA4Tracker } from './ga4TrackerHelper';
import { EnquiryClicked } from './segment/EventName';
import { getCoBrokeEnquiryClickedPayload } from './segment/cobroke';
import { getEnquirerFormat } from './segment/getEnquiryFormat';
import { getRepostWinTrackerPayload } from './segment/repost';

export type EventPayload = {
    // Global Props
    eventName: string;
    excludeObjectGroup?: {
        isExcludeListing?: boolean;
        isExcludeEnquirer?: boolean;
    };

    eventType?: string;
    placementName?: TrackerPlacementName;
    featureType: GA4FeatureType;
    pageType: GA4PageType;
    target?: string;
    properties?: Record<string, any>;
    segmentCommonParams?: Record<string, any>;
    propertiesGA4?: Record<string, any>;
    propertiesGA4Old?: Record<string, any>;
};

type SegmentCommonParamsObject = {
    [key: string]: SegmentCommonParams;
};

type SegmentCommonParams = {
    eventMetric: 'Click' | 'Impression';
    feature: string;
    featureDetail: string;
};

export const useEventTracker = (initialPayload: EventPayload | null = null) => {
    const { authProfile } = useAuth();
    const { listingTrackerData, isRepost } = useListingTracker();
    const { sendTracker, trafficAttributionParams } = useTracker();
    const { listingTrackerGA, listingTrackerGA4New } = useListingTrackerGA();
    const anonymousId = getCookie('ajs_anonymous_id');
    const isMobile = useMediaQuery('(max-width: 768px)');

    const {
        data: {
            otp: { cookies },
        },
    } = useContext(R123EnquiryContext);

    const [payload, setPayload] = useState<
        (EventPayload & Record<string, any>) | null
    >(initialPayload);
    const [trackers, setTrackers] = useState<string[]>([]);

    const generateSegmentCommonParams = (
        featureType: string,
    ): SegmentCommonParams | undefined => {
        try {
            const paramObject: SegmentCommonParamsObject = {
                'LDP Mortgage Calculator': {
                    eventMetric: 'Click',
                    feature: 'Mortgage',
                    featureDetail: 'Calculator',
                },
            };

            return paramObject[featureType];
        } catch (error) {
            return undefined;
        }
    };

    useEffect(() => {
        try {
            if (!payload) return;
            trackers.forEach((tracker) => {
                const [segmentEventName, oldGA4EventName, newGA4EventName] =
                    eventMappings[payload.eventName];

                switch (tracker) {
                    case 'segment':
                        setTimeout(() => {
                            let enquirer = undefined;

                            if (
                                segmentEventName === EnquiryClicked &&
                                isRepost &&
                                listingTrackerData?.listing_id
                            ) {
                                sendTracker(
                                    'Repost Win',
                                    getRepostWinTrackerPayload(
                                        listingTrackerData.listing_id,
                                        authProfile,
                                        isMobile,
                                    ),
                                );
                            }

                            if (
                                segmentEventName === EnquiryClicked &&
                                payload?.cta_button &&
                                payload?.cta_button.includes('Co-broke')
                            ) {
                                sendTracker(
                                    'Co Broke Enquiry Clicked',
                                    getCoBrokeEnquiryClickedPayload(
                                        payload,
                                        listingTrackerData?.listing_id,
                                    ),
                                );
                            }

                            if (
                                [
                                    ListingEvents.Enquiry,
                                    ListingEvents.OTPUnverified,
                                ].includes(payload.eventName as ListingEvents)
                            ) {
                                enquirer = getEnquirerFormat(
                                    authProfile,
                                    cookies,
                                );
                                // no login but have enquiry data intact
                                // treat as logged in user
                                if (
                                    cookies &&
                                    cookies.phoneNumber != '' &&
                                    enquirer.enquirer_phone == ''
                                ) {
                                    enquirer = {
                                        enquirer_email: cookies?.email,
                                        enquirer_logged_in:
                                            enquirer.enquirer_logged_in ||
                                            false,
                                        enquirer_name: cookies?.name ?? '',
                                        enquirer_otp_verified: false,
                                        enquirer_phone: cookies?.phoneNumber,
                                    };
                                }
                            }

                            const userId =
                                (authProfile?.user_type as unknown as AuthUserRoleEnum) ===
                                AuthUserRoleEnum.agent
                                    ? authProfile?.origin_id
                                    : authProfile?.uuid;

                            const generatedParams = generateSegmentCommonParams(
                                payload.featureType,
                            );

                            const segmentCommonParams = generatedParams
                                ? {
                                      deviceType: getDeviceType(),
                                      eventMetric: generatedParams.eventMetric,
                                      feature: generatedParams.feature,
                                      featureDetail:
                                          generatedParams.featureDetail,
                                      pageType: payload.pageType,
                                      platform: isMobile ? 'Mobile' : 'Desktop',
                                      sourceTable: 'Web',
                                      userId,
                                      userType: authProfile?.user_type,
                                      ...payload.segmentCommonParams,
                                  }
                                : undefined;

                            /**
                             * Note: Override listing object data with payload listing data
                             * Sample Case:
                             *  - Recommendation Widget
                             *  - Post Inquiry Suggestion
                             * Override the opened LDP with listing from widget
                             * */
                            const listing = payload.properties?.listing
                                ? payload.properties.listing
                                : listingTrackerData;
                            if (
                                payload.properties &&
                                payload.properties.listing
                            ) {
                                delete payload.properties.listing;
                            }

                            const {
                                eventName,
                                eventType,
                                pageType,
                                featureType,
                                placementName,
                                ...selectedPayload
                            } = payload;

                            let eventNameParam = eventName;
                            if (eventNameParam === 'photo')
                                eventNameParam = 'Photo';
                            if (eventNameParam === 'playVideo')
                                eventNameParam = 'Play Video';
                            if (eventNameParam === 'carousel')
                                eventNameParam = 'Carousel';
                            if (eventNameParam === 'tabClicked')
                                eventNameParam = 'Tab Clicked';

                            sendTracker(
                                segmentEventName,
                                {
                                    event_name: eventNameParam,
                                    placement_name: placementName,
                                    page_type: pageType,
                                    ...selectedPayload.properties,
                                    ...(payload.excludeObjectGroup
                                        ?.isExcludeListing
                                        ? undefined
                                        : listing),
                                    ...(payload.excludeObjectGroup
                                        ?.isExcludeEnquirer
                                        ? undefined
                                        : enquirer),
                                    ...payload.properties,
                                },
                                segmentCommonParams,
                            );
                        }, 200);
                        break;
                    case 'ga4old':
                        if (oldGA4EventName == '') {
                            break;
                        }

                        setTimeout(() => {
                            const eventData = {
                                event: oldGA4EventName,
                                ...listingTrackerGA,
                                featureType: payload.featureType,
                                pageType: payload.pageType,
                                target: payload.target,
                            };
                            gtag.dataLayerPush({
                                ...eventData,
                                ...payload.propertiesGA4Old,
                                event: oldGA4EventName,
                            });
                        }, 200);
                        break;
                    case 'ga4new':
                        setTimeout(() => {
                            sendGA4Tracker({
                                eventAction:
                                    newGA4EventName as GA4EventActionEnum,
                                featureType: payload.featureType,
                                authData: authProfile,
                                pageType: payload.pageType,
                                listing: listingTrackerGA4New,
                                token: anonymousId?.toString() ?? v4(),
                                ...payload.propertiesGA4,
                            });
                        }, 200);
                        break;
                    default:
                        break;
                }
            });
        } catch (error) {
            console.error(
                '[TRACKER MAPPING]',
                `Tracker eventMappings (${payload?.eventName}) invalid or not set. Please see the Errors: ${error}`,
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload, trackers]);

    const trackEvent = (
        newPayload: EventPayload & Record<string, any>,
        newTrackers: string[],
    ) => {
        setPayload(newPayload);
        setTrackers(newTrackers);
    };

    const generateParams = (
        newPayload: EventPayload & Record<string, any>,
        type: Trackers,
    ) => {
        switch (type) {
            case Trackers.Segment:
                const listing = newPayload.properties?.listing
                    ? newPayload.properties.listing
                    : listingTrackerData;

                if (newPayload.properties && newPayload.properties.listing) {
                    delete newPayload.properties.listing;
                }

                return {
                    event_name: newPayload.eventName,
                    placement_name: newPayload.placementName,
                    page_type: newPayload.pageType,
                    anonymous_id: anonymousId,
                    feature_source: newPayload.feature_source,
                    ...payload,
                    ...(newPayload.excludeObjectGroup?.isExcludeListing
                        ? undefined
                        : listing),
                    ...newPayload.properties,
                    ...trafficAttributionParams,
                };
        }
    };

    return { trackEvent, generateParams };
};
